'use client'
import { useEffect, useRef } from 'react'
import DOMManager from '@/utils/DOMManager'
import { APP_NAME, APP_NAMESPACE, TITLE } from '@/constants/AppConfig'

interface StructuredDataProps {
  type: 'homepage' | 'pagination' | 'article'
  data: {
    currentPage?: number
    totalPages?: number
    baseUrl?: string
    article?: {
      title: string
      description: string
      author: string
      publishedDate: string
      modifiedDate?: string
      image?: string
      url: string
      category?: string
      tags?: string[]
    }
  }
}

export default function StructuredData({ type, data }: StructuredDataProps) {
  const cleanupRef = useRef<boolean>(false)

  useEffect(() => {
    const domManager = DOMManager.getInstance()
    cleanupRef.current = false

    // Clean up previous script
    domManager.removeElement(`structured-data-${type}`)

    let structuredData: any = {}

    switch (type) {
      case 'homepage':
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: TITLE,
          url: data.baseUrl,
          description: 'Latest news, breaking stories, and comprehensive coverage from around the world',
          publisher: {
            '@type': 'Organization',
            name: APP_NAME,
            url: data.baseUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${data.baseUrl}//${APP_NAMESPACE}/images/logo.png`,
            },
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${data.baseUrl}/search?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        }
        break

      case 'pagination':
        if (data.currentPage && data.totalPages && data.baseUrl) {
          structuredData = {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `News Articles - Page ${data.currentPage}`,
            description: `Browse news articles on page ${data.currentPage} of ${data.totalPages}`,
            url: data.currentPage === 1 ? data.baseUrl : `${data.baseUrl}/page/${data.currentPage}`,
            isPartOf: {
              '@type': 'WebSite',
              name: APP_NAME,
              url: data.baseUrl,
            },
            mainEntity: {
              '@type': 'ItemList',
              numberOfItems: data.totalPages,
              itemListElement: Array.from({ length: Math.min(data.totalPages, 10) }, (_, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: i === 0 ? data.baseUrl : `${data.baseUrl}/page/${i + 1}`,
                name: `Page ${i + 1}`,
              })),
            },
          }
        }
        break

      case 'article':
        if (data.article) {
          structuredData = {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: data.article.title,
            description: data.article.description,
            url: data.article.url,
            datePublished: data.article.publishedDate,
            dateModified: data.article.modifiedDate || data.article.publishedDate,
            author: {
              '@type': 'Person',
              name: data.article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: APP_NAME,
              url: data.baseUrl,
              logo: {
                '@type': 'ImageObject',
                url: `${data.baseUrl}//${APP_NAMESPACE}/images/logo.png`,
                width: 512,
                height: 512,
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': data.article.url,
            },
            ...(data.article.image && {
              image: {
                '@type': 'ImageObject',
                url: data.article.image,
                width: 1200,
                height: 630,
              },
            }),
            ...(data.article.category && {
              articleSection: data.article.category,
            }),
            ...(data.article.tags &&
              data.article.tags.length > 0 && {
                keywords: data.article.tags.join(', '),
              }),
          }
        }
        break
    }

    if (Object.keys(structuredData).length > 0 && !cleanupRef.current) {
      // Validate that the structured data has required fields
      const isValid =
        (type === 'homepage' && structuredData['@type'] === 'WebSite' && structuredData.name && structuredData.url) ||
        (type === 'pagination' &&
          structuredData['@type'] === 'CollectionPage' &&
          structuredData.name &&
          structuredData.url) ||
        (type === 'article' &&
          structuredData['@type'] === 'NewsArticle' &&
          structuredData.headline &&
          structuredData.url &&
          structuredData.datePublished &&
          structuredData.author)

      if (isValid) {
        const script = document.createElement('script')
        script.id = `structured-data-${type}`
        script.type = 'application/ld+json'
        script.textContent = JSON.stringify(structuredData)
        domManager.addElement(`structured-data-${type}`, script)
      } else {
        console.warn(`Invalid structured data for type: ${type}`, structuredData)
      }
    }

    // Cleanup function
    return () => {
      cleanupRef.current = true
      domManager.removeElement(`structured-data-${type}`)
    }
  }, [type, data])

  return null
}
